import React, { useState } from 'react';
import axios from 'axios';
import { Pill, Search, AlertTriangle, CheckCircle2, Info, ChevronDown, ChevronUp, Sparkles, ShieldCheck, Ban, Database } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { VoiceInput } from '../components/VoiceInput';
import { PageHeader } from '../components/PageHeader';
import { AIResultSections } from '../components/AIResultSections';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const CommercBadge = ({ etat }) => {
  const isOk = /commercialis/i.test(etat || '');
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${isOk ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
      {isOk ? <CheckCircle2 className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
      {etat || 'Statut inconnu'}
    </span>
  );
};

export const MedicationsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState(null);
  const [meta, setMeta] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const [expanded, setExpanded] = useState(null);
  const [enrichments, setEnrichments] = useState({});
  const [enrichLoading, setEnrichLoading] = useState(null);

  const [interactionMeds, setInteractionMeds] = useState(['', '']);
  const [interactionResult, setInteractionResult] = useState(null);
  const [interactionResolved, setInteractionResolved] = useState([]);
  const [interactionLoading, setInteractionLoading] = useState(false);

  const handleSearch = async (e) => {
    e?.preventDefault();
    const q = searchQuery.trim();
    if (q.length < 2) {
      toast.error('Saisissez au moins 2 caractères');
      return;
    }
    setSearchLoading(true);
    setExpanded(null);
    try {
      const response = await axios.get(`${API}/medications/bdpm/search`, { params: { q } });
      setResults(response.data.results || []);
      setMeta(response.data.meta || null);
    } catch (error) {
      toast.error('Erreur lors de la recherche BDPM');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleVoiceTranscription = (text) => {
    setSearchQuery(text);
    setTimeout(() => handleSearch(), 300);
  };

  const toggleEnrich = async (cis) => {
    if (expanded === cis) {
      setExpanded(null);
      return;
    }
    setExpanded(cis);
    if (enrichments[cis]) return;
    setEnrichLoading(cis);
    try {
      const response = await axios.post(`${API}/medications/enrich`, { cis });
      setEnrichments((prev) => ({ ...prev, [cis]: response.data.enrichment }));
    } catch (error) {
      toast.error("Erreur lors de l'enrichissement clinique");
      setExpanded(null);
    } finally {
      setEnrichLoading(null);
    }
  };

  const handleCheckInteractions = async () => {
    const validMeds = interactionMeds.filter((med) => med.trim());
    if (validMeds.length < 2) {
      toast.error('Veuillez entrer au moins 2 médicaments');
      return;
    }
    setInteractionLoading(true);
    try {
      const response = await axios.post(`${API}/medications/interactions`, { medications: validMeds });
      setInteractionResult(response.data.interactions);
      setInteractionResolved(response.data.resolved || []);
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    } finally {
      setInteractionLoading(false);
    }
  };

  const addMedicationField = () => setInteractionMeds([...interactionMeds, '']);
  const updateMedicationField = (index, value) => {
    const newMeds = [...interactionMeds];
    newMeds[index] = value;
    setInteractionMeds(newMeds);
  };
  const removeMedicationField = (index) => {
    if (interactionMeds.length > 1) setInteractionMeds(interactionMeds.filter((_, i) => i !== index));
  };

  return (
    <div data-testid="medications-page" className="space-y-4 sm:space-y-8">
      <PageHeader icon={Pill} title="Base Médicamenteuse" subtitle="Recherche réelle (BDPM officielle) et interactions" gradient="from-cyan-600 to-blue-600" testId="medications-header" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
        {/* Medication Search — REAL BDPM */}
        <div className="glassmorphic rounded-2xl p-3.5 sm:p-6">
          <h2 className="text-base sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
            <Search className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
            Recherche de Médicament
          </h2>

          <form onSubmit={handleSearch} className="space-y-3">
            <input
              data-testid="medication-search-input"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Nom commercial ou molécule (DCI)..."
              className="w-full px-4 py-3 min-h-[48px] bg-slate-950/50 border border-slate-800 rounded-xl text-slate-200 text-[15px] sm:text-base placeholder:text-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            <div className="flex gap-2 sm:gap-3">
              <VoiceInput onTranscription={handleVoiceTranscription} placeholder="Parler" disabled={searchLoading} className="flex-shrink-0" />
              <Button
                data-testid="search-medication-button"
                type="submit"
                disabled={searchLoading}
                className="flex-1 min-h-[48px] rounded-xl px-6 py-3 bg-cyan-600 hover:bg-cyan-500 text-white font-medium transition-all"
              >
                {searchLoading ? 'Recherche...' : 'Rechercher'}
              </Button>
            </div>
          </form>

          {meta && (
            <div data-testid="bdpm-meta" className="mt-3 flex items-start gap-1.5 text-[11px] text-slate-500">
              <Database className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-cyan-500/70" />
              <span>
                Source : Base de Données Publique des Médicaments (BDPM — ANSM/HAS/UNCAM) • base du {meta.version_date} • {meta.specialites_count?.toLocaleString('fr-FR')} spécialités
              </span>
            </div>
          )}

          {results !== null && (
            <div data-testid="search-result" className="mt-4 space-y-2.5">
              {results.length === 0 ? (
                <p className="text-slate-400 text-sm py-4 text-center">Non trouvé dans la base BDPM.</p>
              ) : (
                results.map((r) => (
                  <div key={r.cis} data-testid={`bdpm-result-${r.cis}`} className="bg-slate-950/40 border border-slate-800 rounded-xl overflow-hidden">
                    <div className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-white font-medium text-[15px] leading-snug">{r.denomination}</h3>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {r.substances_noms?.map((s) => (
                          <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300">{s}</span>
                        ))}
                        {r.generique && (
                          <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.generique.type === 'princeps' ? 'bg-violet-500/15 text-violet-300' : 'bg-sky-500/15 text-sky-300'}`}>
                            {r.generique.type === 'princeps' ? 'Princeps' : 'Générique'}
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 text-xs text-slate-400">
                        {[r.forme, r.voies].filter(Boolean).join(' • ')}
                        {r.titulaire ? ` — ${r.titulaire}` : ''}
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <CommercBadge etat={r.commercialisation} />
                        {r.taux_remb && <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300">Remb. {r.taux_remb.replace(/\s/g, '')}</span>}
                        {r.prix && <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-700/60 text-slate-200">{r.prix} €</span>}
                      </div>
                      <button
                        data-testid={`enrich-btn-${r.cis}`}
                        onClick={() => toggleEnrich(r.cis)}
                        className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-cyan-600/15 hover:bg-cyan-600/25 text-cyan-300 text-sm font-medium transition-all"
                      >
                        <Sparkles className="w-4 h-4" />
                        {expanded === r.cis ? 'Masquer l’enrichissement' : 'Enrichissement clinique IA'}
                        {expanded === r.cis ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                    {expanded === r.cis && (
                      <div data-testid={`medication-enrichment-${r.cis}`} className="border-t border-slate-800 p-3 bg-slate-950/30">
                        <div className="flex items-center gap-1.5 mb-2 text-[11px] text-amber-400/90">
                          <Info className="w-3.5 h-3.5" />
                          Enrichissement généré par IA — à vérifier avec le RCP officiel
                        </div>
                        {enrichLoading === r.cis ? (
                          <p className="text-slate-400 text-sm py-3">Génération de l’enrichissement…</p>
                        ) : (
                          <AIResultSections text={enrichments[r.cis] || ''} accent="cyan" testId={`enrichment-sections-${r.cis}`} rateFeature="medication_enrichment" rateTargetId={r.cis} />
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Interaction Checker */}
        <div className="glassmorphic rounded-2xl p-3.5 sm:p-6">
          <h2 className="text-base sm:text-2xl font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
            Vérificateur d'Interactions
          </h2>

          <div className="space-y-3 sm:space-y-4">
            {interactionMeds.map((med, index) => (
              <div key={`med-field-${index}`} className="flex gap-2">
                <input
                  data-testid={`interaction-med-${index}`}
                  type="text"
                  value={med}
                  onChange={(e) => updateMedicationField(index, e.target.value)}
                  placeholder={`Médicament ${index + 1}`}
                  className="flex-1 min-w-0 px-4 py-3 min-h-[48px] bg-slate-950/50 border border-slate-800 rounded-xl text-slate-200 text-[15px] sm:text-base placeholder:text-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                {interactionMeds.length > 1 && (
                  <button
                    type="button"
                    aria-label="Retirer ce médicament"
                    onClick={() => removeMedicationField(index)}
                    className="w-12 min-h-[48px] flex items-center justify-center rounded-xl bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 border border-rose-500/20 transition-all text-xl"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}

            <button
              data-testid="add-medication-field"
              type="button"
              onClick={addMedicationField}
              className="w-full px-4 py-2 min-h-[44px] rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 transition-all"
            >
              + Ajouter un médicament
            </button>

            <Button
              data-testid="check-interactions-button"
              onClick={handleCheckInteractions}
              disabled={interactionLoading}
              className="w-full min-h-[48px] rounded-full px-6 py-3 bg-amber-600 hover:bg-amber-500 text-white font-medium transition-all"
            >
              {interactionLoading ? 'Vérification...' : 'Vérifier les Interactions'}
            </Button>
          </div>

          {interactionResult && (
            <div data-testid="interaction-result" className="mt-4 sm:mt-6">
              {interactionResolved.length > 0 && (
                <div data-testid="interaction-resolved" className="mb-3 space-y-1">
                  {interactionResolved.map((r) => (
                    <div key={r.input} className="flex items-center gap-2 text-xs">
                      {r.found ? <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" /> : <Ban className="w-3.5 h-3.5 text-rose-400 flex-shrink-0" />}
                      <span className="text-slate-400">{r.input}</span>
                      {r.found ? (
                        <span className="text-slate-300 truncate">→ {r.matched}{r.dci?.length ? ` (${r.dci.join(', ')})` : ''}</span>
                      ) : (
                        <span className="text-rose-400">non trouvé en BDPM</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 mb-2.5 sm:mb-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <h3 className="text-base sm:text-lg font-semibold text-white">Résultats des Interactions</h3>
              </div>
              <AIResultSections text={interactionResult} accent="amber" testId="interaction-result-sections" rateFeature="medication_interactions" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
